import React, { useEffect, useMemo, useState, useRef } from "react";
import "./header.css";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import closeIcon from "../../assets/icons/close_icon.png";
import CalenderIconForCampaignList from "../../assets/icons/calender-icon-for-campaignlist.svg";
import selectSideIcon from "../../assets/icons/select-btn-side-grey-icon.png";
import { useGlobalContext } from "../../context/global";
import HeaderDrawer from "./HeaderDrawer";
import {
  hideCalendarPages,
  hideCalendarSearchQuery,
} from "../../constant/hideCalendarOnDifferentPages";
import CalenderComponent from "../calender/Calender";
import {
  getClientDataFromAdyogiAPI,
  getPlatformStatus,
} from "../../helper/clientData/utils";
import {
  buildCompareRangesFromSelection,
  getDefaultCalendarDate,
  getTodayCalendarDate,
} from "../../constant/calendarDefaultConfigurations";
import amazonLogo from "../../assets/svgs/amazon.svg";
import myntraLogo from "../../assets/svgs/myntra.svg";
import flipkartLogo from "../../assets/svgs/flipkart.svg";
import PlatformTab from "./PlatformTab";

function Header(props) {
  const [drawer, setDrawer] = useState(false);
  const calendarRef = useRef(null);
  const [showCustomDateBox, setShowCustomDateBox] = useState(false);

  const localCalendar = Boolean(props.localCalendar);
  const {
    dateDifference,
    clientId,
    calendarFreeze,
    setShowCMSLink,
    clientData,
    selectedPlatform,
    headerCalendarHandleComparisonView,
    headerCalendarHideComparisonSwitch,
  } = useGlobalContext();

  let storedPlatform = null;
  try {
    storedPlatform = JSON.parse(localStorage.getItem("user_platform"));
  } catch (e) {
    storedPlatform = null;
  }
  const activePlatform = selectedPlatform || storedPlatform;
  const includeToday =
    props.includeToday !== undefined
      ? Boolean(props.includeToday)
      : activePlatform === "amazon";

  const handleComparisonView = headerCalendarHandleComparisonView;
  const hideComparisonSwitch = headerCalendarHideComparisonSwitch;
  const location = useLocation();
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );

  const initialLocalRange = useMemo(
    () =>
      props.defaultCalendarDate ??
      (includeToday ? getTodayCalendarDate() : getDefaultCalendarDate(false)),
    // Intentional: initialize once per mount for local calendar pages
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialLocalLabel =
    props.defaultDateDifference ?? (includeToday ? "Today" : "Last 7 Days");

  const [localCalendarDate, setLocalCalendarDate] = useState(initialLocalRange);
  const [localDateDifference, setLocalDateDifference] =
    useState(initialLocalLabel);
  const [localOldCalendarData, setLocalOldCalendarData] =
    useState(initialLocalRange);
  const [localOldDateDifference, setLocalOldDateDifference] =
    useState(initialLocalLabel);
  const [localApplyCalendar, setLocalApplyCalendar] = useState(1);
  const [localMultiCalendarDate, setLocalMultiCalendarDate] = useState(() =>
    buildCompareRangesFromSelection(initialLocalRange[0])
  );

  const localCalendarState = localCalendar
    ? {
        calendarDate: localCalendarDate,
        setCalendarDate: setLocalCalendarDate,
        dateDifference: localDateDifference,
        setDateDifference: setLocalDateDifference,
        oldCalendarData: localOldCalendarData,
        setOldCalendarData: setLocalOldCalendarData,
        oldDateDifference: localOldDateDifference,
        setOldDateDifference: setLocalOldDateDifference,
        applyCalendar: localApplyCalendar,
        setApplyCalendar: setLocalApplyCalendar,
        multiCalendarDate: localMultiCalendarDate,
        setMultiCalendarDate: setLocalMultiCalendarDate,
      }
    : null;

  useEffect(() => {
    if (!localCalendar) return;
    props.onLocalCalendarChange?.({
      calendarDate: localCalendarDate,
      dateDifference: localDateDifference,
      applyCalendar: localApplyCalendar,
      multiCalendarDate: localMultiCalendarDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localCalendar,
    localCalendarDate,
    localDateDifference,
    localApplyCalendar,
    localMultiCalendarDate,
  ]);

  const shouldHideCalendar = (loc) => {
    const isSearchMatch = hideCalendarSearchQuery.some((query) =>
      loc.search.includes(query)
    );
    const isPathMatch = hideCalendarPages.includes(loc.pathname);
    return isSearchMatch || isPathMatch;
  };

  const isCalendarHidden = !shouldHideCalendar(location);

  useEffect(() => {
    const fetchData = async (passedClientId) => {
      try {
        const response = await getClientDataFromAdyogiAPI(passedClientId);
        const productType =
          response?.data?.results?.[0]?.amazon_setup?.product_type;
        localStorage.setItem(
          "product_type",
          JSON.stringify(productType ?? "internal")
        );
        if (productType === "Self serve") {
          setShowCMSLink(false);
        } else {
          setShowCMSLink(true);
        }
      } catch (error) {
        throw error;
      }
    };
    fetchData(clientId);
  }, [clientId, setShowCMSLink]);

  const closeCalender = (e) => {
    if (
      calendarRef.current &&
      showCustomDateBox &&
      !calendarRef.current.contains(e.target)
    ) {
      setShowCustomDateBox(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", closeCalender);
    return () => document.removeEventListener("mousedown", closeCalender);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomDateBox]);

  const { myntraStatus, flipkartStatus, amazonStatus } =
    getPlatformStatus(clientData ?? {});
  const tabNames = [
    { name: "Amazon", src: amazonLogo, isDisabled: !amazonStatus },
    { name: "Flipkart", src: flipkartLogo, isDisabled: !flipkartStatus },
    { name: "Myntra", src: myntraLogo, isDisabled: !myntraStatus },
  ];

  const displayedDateDifference = localCalendar
    ? localDateDifference
    : dateDifference;

  const calendarButton = (
    <button
      type="button"
      className={`date-container date-btn-campaignlist ${calendarFreeze && !localCalendar ? "disable-pointer-events" : ""}`}
      onClick={() => setShowCustomDateBox(!showCustomDateBox)}
    >
      <div className="date-btn-inner">
        <span className="date-btn-text">{displayedDateDifference}</span>
      </div>
      <div className="btn-calender-img-holder">
        <span>
          <img
            className="dropdown-img-for-select"
            src={selectSideIcon}
            alt="Select"
          />
          <img
            className="calender-icon-for-campaignlist"
            src={CalenderIconForCampaignList}
            alt="Calendar"
          />
        </span>
      </div>
    </button>
  );

  const calendarDropdown = (
    <div
      ref={calendarRef}
      className={
        showCustomDateBox ? "calender-inclasslist-container" : "hide"
      }
    >
      <CalenderComponent
        setShowCustomDateBox={setShowCustomDateBox}
        handleComparisonView={localCalendar ? props.handleComparisonView : handleComparisonView}
        hideComparisonSwitch={localCalendar ? props.hideComparisonSwitch : hideComparisonSwitch}
        includeToday={includeToday}
        localCalendar={localCalendar}
        localCalendarState={localCalendarState}
      />
      <span
        className={`calender-close-icon-campaignlist ${calendarFreeze && !localCalendar ? "disable-pointer-events" : ""}`}
      >
        <img
          className={calendarFreeze && !localCalendar ? "disable-pointer-events" : ""}
          onClick={() => setShowCustomDateBox(false)}
          src={closeIcon}
          alt="Close"
        />
      </span>
    </div>
  );

  // Show-only calendar mode (e.g. for page-level calendar)
  if (props?.showOnlyCalendar) {
    return (
      <div className="header-calendar-wrap">
        {calendarButton}
        {calendarDropdown}
      </div>
    );
  }

  return (
    <div
      className={`header-container ${dashboardSideBarVisibiltyState ? "header-subpanel-open" : ""}`}
    >
      <header className="header-inner" role="banner">
        <div
          className={`header-section header-left ${dashboardSideBarVisibiltyState ? "serch-bar-container-onopen-sidebar" : "serch-bar-container-close-sidebar"}`}
        >
          <div className="platform-tabs-container">
            <PlatformTab platforms={tabNames} />
          </div>
        </div>

        <div className="header-section header-right">
          {isCalendarHidden && (
            <div className="header-calendar-wrap">
              {calendarButton}
              {calendarDropdown}
            </div>
          )}
          <div className="profile-container">
            <HeaderDrawer setDrawer={setDrawer} drawer={drawer} />
          </div>
        </div>
      </header>
    </div>
  );
}

export default Header;
